import React, { useState, useEffect } from 'react';
import { Container, Form, Button, Row, Col, Table, Alert, Card } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { FaPlus, FaTrash, FaGripVertical, FaStar } from 'react-icons/fa';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import LoadingSpinner from '../../components/LoadingSpinner';
import { fetchProductById, clearCurrentProduct, showNotification } from '../../store/actions';
import productService from '../../services/productService';
import categoryService from '../../services/categoryService';
import useTitle from '../../hooks/useTitle';
import defaultProductImage from '../../images/default-product.png';

const AdminProductEdit = () => {
    const { data: product, loading, error } = useSelector(state => state.currentProduct);
    const { data: categories } = useSelector(state => state.categories.all);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const isNew = id === 'new';
    
    // Задаем название сайта
    useTitle(isNew ? 'Создание нового товара | Админ-панель' : 'Редактирование товара | Админ-панель');

    // Состояние формы
    const [formData, setFormData] = useState({
        id: null,
        name: '',
        title: '',
        category: '',
        categoryTitle: '',
        price: '',
        description: '',
        articles: [{ code: '', name: '' }],
        images: [],
        characteristics: []
    });

    // Состояние для работы с формой
    const [categoryLoading, setCategoryLoading] = useState(false);
    const [localLoading, setLocalLoading] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [categoryCharacteristics, setCategoryCharacteristics] = useState([]);
    const [imageFiles, setImageFiles] = useState([]);
    const [isDragging, setIsDragging] = useState(false);

    // Загрузка данных товара если это редактирование
    useEffect(() => {
        // Очищаем старый продукт перед загрузкой нового
        dispatch(clearCurrentProduct());
        if (!isNew && id && !isNaN(id)) {
            dispatch(fetchProductById(id));
        }
    }, [id, dispatch]);

    // Обновление формы при загрузке данных товара
    useEffect(() => {
        if (product && !isNew) {
            setFormData({
                id: product.id,
                name: product.name,
                title: product.title,
                category: product.category,
                categoryTitle: product.categoryTitle,
                price: product.price,
                description: product.description,
                articles: product.articles && product.articles.length 
                    ? product.articles.map(a => ({ code: a.code, name: a.name })) 
                    : [{ code: '', name: '' }],
                images: product.images || [],
                // characteristics: product.characteristics || []
            });

            if (product.category && categories) {
                const category = categories.find(c => c.name === product.category);
                if (category) {
                    setSelectedCategory(category);
                    fetchCategoryCharacteristics(category.id, product.characteristics);
                    // setTimeout(() => fetchCategoryCharacteristics(category.id, characteristics), 100);
                }
            } else {
                setFormData(prevData => ({
                    ...prevData,
                    characteristics: product.characteristics || []
                }));
            }
        }
    }, [product, isNew, categories]);
    
    // Получение характеристик выбранной категории
    const fetchCategoryCharacteristics = async (categoryId, existingCharacteristics = []) => {
        setCategoryLoading(true);
        const result = await categoryService.fetchCategoryById(categoryId);
         if (result.success) {
            const characteristics = result.data.categoryCharacteristics || [];
            setCategoryCharacteristics(characteristics);
            
            const existingMap = {};
            existingCharacteristics.forEach(char => {
                existingMap[char.categoryCharacteristicId] = char.value;
            });

            const newCharacteristics = characteristics.map(catChar => ({
                categoryCharacteristicId: catChar.id,
                name: catChar.name,
                value: existingMap[catChar.id] || ''
            }));
            
            setFormData(prevData => ({
                ...prevData,
                characteristics: newCharacteristics
            }));

            setCategoryLoading(false);
        }
        else {
            dispatch(showNotification('Ошибка при получении характеристик категории', 'error'));
            setCategoryLoading(false);
        }
    };

    // Обработчики изменения полей формы
    const handleChange = (e) => {
        const { name, value } = e.target;

        // Специальная обработка для поля цены
        if (name === 'price') {
            // Разрешаем только цифры, запятую и точку
            let cleanedValue = value.replace(/[^0-9.,]/g, '');

            // Заменяем точку на запятую (единый формат)
            cleanedValue = cleanedValue.replace(/\./g, ',');

            // Удаляем все лишние запятые, оставляем только первую
            const parts = cleanedValue.split(',');
            const formattedValue = parts.length > 1
                ? `${parts[0]},${parts.slice(1).join('')}`
                : cleanedValue;
                
            setFormData({
                ...formData,
                [name]: formattedValue
            });
        } else {
            // Обработка других полей остается прежней
            setFormData({
                ...formData,
                [name]: value
            });
        }
        
        // Очистка ошибки валидации при изменении поля
        if (validationErrors[name]) {
            setValidationErrors({
                ...validationErrors,
                [name]: ''
            });
        }
    };

    // Обработчик изменения категории
    const handleCategoryChange = (e) => {
        const categoryId = parseInt(e.target.value);
        
        if (!categoryId) {
            setSelectedCategory(null);
            setCategoryCharacteristics([]);
            setFormData(prevData => ({
                ...prevData,
                category: '',
                categoryTitle: '',
                characteristics: []
            }));
            return;
        }
        
        const category = categories.find(c => c.id === categoryId);
        if (category) {
            setSelectedCategory(category);
            setFormData(prevData => ({
                ...prevData,
                category: category.name,
                categoryTitle: category.title
            }));
            fetchCategoryCharacteristics(categoryId);
        }
    };

    // Обработчик изменения характеристик
    const handleCharacteristicChange = (id, value) => {
        const updatedCharacteristics = formData.characteristics.map(char => {
            if (char.categoryCharacteristicId === id) {
                return { ...char, value };
            }
            return char;
        });
        
        setFormData({
            ...formData,
            characteristics: updatedCharacteristics
        });
    };

    // Обработчик изменения артикулов
    const handleArticleChange = (index, field, value) => {
        const updatedArticles = [...formData.articles];
        updatedArticles[index] = {
            ...updatedArticles[index],
            [field]: value
        };
        
        setFormData({
            ...formData,
            articles: updatedArticles
        });
    };

    // Добавление нового артикула
    const handleAddArticle = () => {
        setFormData({
            ...formData,
            articles: [...formData.articles, { code: '', name: '' }]
        });
    };

    // Удаление артикула
    const handleRemoveArticle = (index) => {
        if (formData.articles.length === 1) {
            dispatch(showNotification('Должен быть указан хотя бы один артикул', 'warning'));
            return;
        }
        
        const updatedArticles = [...formData.articles];
        updatedArticles.splice(index, 1);
        
        setFormData({
            ...formData,
            articles: updatedArticles
        });
    };

    // Обработчик загрузки изображений
    const handleImageUpload = (e) => {
        const files = Array.from(e.target.files);
        
        if (formData.images.length + files.length > 4) {
            dispatch(showNotification('Максимальное количество изображений - 4', 'warning'));
            return;
        }
        
        // Проверка файлов
        const allowedExtensions = ['.jpg', '.jpeg', '.png'];
        const maxSize = 10 * 1024 * 1024; // 10 МБ
        
        const validFiles = files.filter(file => {
            const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
            if (!allowedExtensions.includes(fileExtension)) {
                dispatch(showNotification(`Файл ${file.name}: неподдерживаемый формат`, 'error'));
                return false;
            }
            
            if (file.size > maxSize) {
                dispatch(showNotification(`Файл ${file.name}: превышен размер 10 МБ`, 'error'));
                return false;
            }
            
            return true;
        });
        
        if (validFiles.length === 0) return;
        
        // Добавляем новые файлы
        setImageFiles(prev => [...prev, ...validFiles]);
        
        // Создаем превью для новых изображений
        const newImages = validFiles.map((file, index) => ({
            file,
            tempId: Date.now() + index, // Временный ID для идентификации
            imagePath: URL.createObjectURL(file),
            displayOrder: formData.images.length + index,
            isMain: formData.images.length === 0 && index === 0 // Первое изображение делаем главным если нет других
        }));
        
        setFormData(prev => ({
            ...prev,
            images: [...prev.images, ...newImages]
        }));
        
        // Сбрасываем значение input file
        e.target.value = '';
    };

    // Удаление изображения
    const handleRemoveImage = (index) => {
        const updatedImages = [...formData.images];
        const removedImage = updatedImages[index];
        
        // Если удаляем главное изображение и есть другие, делаем другое главным
        if (removedImage.isMain && updatedImages.length > 1) {
            const newMainIndex = index === 0 ? 1 : 0;
            updatedImages[newMainIndex] = {
                ...updatedImages[newMainIndex],
                isMain: true
            };
        }
        
        // Удаляем URL для предотвращения утечек памяти
        if (removedImage.tempId && removedImage.imagePath.startsWith('blob:')) {
            URL.revokeObjectURL(removedImage.imagePath);
        }
        
        // Удаляем изображение
        updatedImages.splice(index, 1);
        
        // Обновляем порядок отображения
        const reorderedImages = updatedImages.map((img, idx) => ({
            ...img,
            displayOrder: idx
        }));
        
        // Удаляем из списка файлов если это новый файл
        if (removedImage.file) {
            setImageFiles(prev => prev.filter(file => 
                !(removedImage.tempId && file === removedImage.file)
            ));
        }
        
        setFormData({
            ...formData,
            images: reorderedImages
        });
    };

    // Установка главного изображения
    const handleSetMainImage = (index) => {
        const updatedImages = formData.images.map((img, idx) => ({
            ...img,
            isMain: idx === index
        }));
        
        setFormData({
            ...formData,
            images: updatedImages
        });
    };

    // Обработчики drag-n-drop для изображений
    const handleDragStart = () => {
        setIsDragging(true);
    };

    const handleDragEnd = (result) => {
        setIsDragging(false);
        if (!result.destination) return;
        
        const items = Array.from(formData.images);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);
        
        // Обновляем порядок отображения
        const reorderedImages = items.map((img, idx) => ({
            ...img,
            displayOrder: idx
        }));
        
        setFormData({
            ...formData,
            images: reorderedImages
        });
    };

    // Валидация формы
    const validateForm = () => {
        const errors = {};
        
        // Проверка названия
        if (!formData.title?.trim()) {
            errors.title = 'Необходимо указать название товара';
        } else if (formData.title.trim().length < 2) {
            errors.title = 'Название товара должно содержать не менее 2 символов';
        } else if (formData.title.trim().length > 200) {
            errors.title = 'Название товара не должно превышать 200 символов';
        }
        
        // Проверка категории
        if (!formData.category) {
            errors.category = 'Необходимо выбрать категорию товара';
        }
         
        // Проверка цены
        if (formData.price) {
            if (isNaN(parseFloat(formData.price))) {
                errors.price = 'Цена должна быть числом';
            } else if (parseFloat(formData.price) <= 0) {
                errors.price = 'Цена должна быть положительным числом';
            } else if (parseFloat(formData.price) >= 10000000) {
                errors.price = 'Цена должна быть менее 10 000 000';
            }
        }

        // Проверка описания
        if (formData.description && formData.description.length > 5000) {
            errors.description = 'Описание не должно превышать 5000 символов';
        }
        
        // Проверка артикулов
        let hasEmptyRequired = false;
        const multipleArticles = formData.articles.length > 1;
        // Проверка уникальности кодов артикулов
        const articleCodes = new Set();
        const duplicateCodes = new Set();
        const codesWithHyphens = [];

        formData.articles.forEach(article => {
            if (article.code.trim()) {
                if (articleCodes.has(article.code.trim())) {
                    duplicateCodes.add(article.code.trim());
                } else {
                    articleCodes.add(article.code.trim());
                }

                // Проверка на наличие дефиса в коде артикула
                if (article.code.trim().includes('-')) {
                    codesWithHyphens.push(article.code.trim());
                }
            }
        });

        formData.articles.forEach((article, index) => {
            // Проверка кода артикула
            if (!article.code.trim()) {
                hasEmptyRequired = true;
                errors[`article_${index}_code`] = 'Необходимо указать код артикула';
            } else if (duplicateCodes.has(article.code.trim())) {
                // Добавляем ошибку для дублирующихся кодов
                errors[`article_${index}_code`] = 'Код артикула должен быть уникальным';
                hasEmptyRequired = true; // Используем тот же флаг для отображения общей ошибки
            } else if (article.code.trim().includes('-')) {
                // Добавляем ошибку для кодов с дефисами
                errors[`article_${index}_code`] = 'Код артикула не должен содержать дефис';
                hasEmptyRequired = true;
            }
            
            // Проверка названия артикула (обязательно только при наличии нескольких артикулов)
            if (multipleArticles && !article.name.trim()) {
                hasEmptyRequired = true;
                errors[`article_${index}_name`] = 'При наличии нескольких артикулов необходимо указать название';
            }
        });
        
        // Общая ошибка по артикулам с учётом дубликатов
        if (duplicateCodes.size > 0) {
            errors.articles = `Коды артикулов должны быть уникальными. Повторяющиеся коды: ${Array.from(duplicateCodes).join(', ')}`;
        } else if (codesWithHyphens.length > 0) {
            errors.articles = `Коды артикулов не должны содержать дефис (-). Проблемные коды: ${codesWithHyphens.join(', ')}`;
        } else if (hasEmptyRequired) {
            errors.articles = multipleArticles 
                ? 'Заполните все обязательные поля артикулов (код и название)' 
                : 'Заполните код артикула';
        }
        
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Отправка формы
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            dispatch(showNotification('Пожалуйста, исправьте ошибки в форме', 'error'));
            return;
        }

        setLocalLoading(true);

        // Подготовка данных для отправки
        const productData = new FormData();
        
        // Основные поля
        productData.append('title', formData.title);
        productData.append('categoryId', categories.find(c => c.name === formData.category).id);

        if (formData.price) {
            productData.append('price', formData.price.toString().replace('.', ','));
        }
        if (formData.description) {
            productData.append('description', formData.description);
        }
            
        // Артикулы
        productData.append('articles', JSON.stringify(formData.articles));
        // Характеристики
        const nonEmptyCharacteristics = formData.characteristics.filter(char => char.value.trim() !== '');
        productData.append('characteristics', JSON.stringify(nonEmptyCharacteristics));
            
        // Существующие изображения
        const existingImages = formData.images
            .filter(img => !img.tempId)
            .map(img => ({
                imagePath: img.imagePath,
                displayOrder: img.displayOrder,
                isMain: img.isMain
            }));
            
        if (existingImages.length > 0) {
            productData.append('existingImagesInfo', JSON.stringify(existingImages));
        }
            
        // Добавляем новые файлы изображений
        imageFiles.forEach((file, index) => {
            productData.append('images', file);
        });
            
        // Информация о новых изображениях
        const newImagesInfo = formData.images
            .filter(img => img.tempId)
            .map(img => ({
                index: imageFiles.findIndex(file => img.file === file),
                displayOrder: img.displayOrder,
                isMain: img.isMain
            }));
            
        if (newImagesInfo.length > 0) {
            productData.append('newImagesInfo', JSON.stringify(newImagesInfo));
        }
            
        let result;
        if (isNew) {
            result = await productService.createProduct(productData);
        } else {
            // Обновление существующего товара
            // productData.append('id', formData.id);
            result = await productService.updateProduct(formData.id, productData);
        }

        if (result.success) {
            dispatch(showNotification(isNew ? 'Товар успешно создан' : 'Товар успешно обновлен', 'success'));
            navigate('/performance-admin-panel/products');
            setLocalLoading(false);
        } else {
            dispatch(showNotification(result.error, 'error'));
            setLocalLoading(false);
        }
    }

    // Отмена и возврат к списку товаров
    const handleCancel = () => {
        navigate('/performance-admin-panel/products');
    };

    return (
        <Container className="py-4"> 
            <h2 className="mb-4">{isNew ? 'Создание нового товара' : 'Редактирование товара'}</h2>
            {loading || localLoading ? (
                <LoadingSpinner message={localLoading ? 'Сохранение данных...' : (isNew ? 'Создание товара...' : 'Загрузка данных товара...')}  containerHeight="vh-50"/>
            ) : error ? (
                <div className="alert alert-danger">
                    {error}
                </div>
            ) : (
                <Form onSubmit={handleSubmit}>
                    <Row>
                        <Col md={8}>
                            <Form.Group className="mb-4">
                                <Form.Label>Название <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleChange}
                                    isInvalid={!!validationErrors.title}
                                    placeholder="Введите название товара"
                                    required
                                />
                                <Form.Control.Feedback type="invalid">
                                    {validationErrors.title}
                                </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group className="mb-4">
                                <Form.Label>Категория <span className="text-danger">*</span></Form.Label>
                                <Form.Select 
                                    name="category"
                                    value={selectedCategory ? selectedCategory.id : ''}
                                    onChange={handleCategoryChange}
                                    isInvalid={!!validationErrors.category}
                                    required
                                >
                                    <option value="">Выберите категорию</option>
                                    {categories && categories.map(category => (
                                        <option key={category.id} value={category.id}>
                                            {category.title}
                                        </option>
                                    ))}
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                    {validationErrors.category}
                                </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group className="mb-4">
                                <Form.Label>Цена (₽)</Form.Label>
                                <Form.Control
                                    type="text"
                                    inputMode="decimal"
                                    pattern="^\d*(\.|,)?\d*$"
                                    name="price"
                                    value={formData.price}
                                    onChange={handleChange}
                                    isInvalid={!!validationErrors.price}
                                    placeholder="Оставьте пустым, если 'Цена по запросу'"
                                />
                                <Form.Control.Feedback type="invalid">
                                    {validationErrors.price}
                                </Form.Control.Feedback>
                                <Form.Text className="text-muted">
                                    Укажите цену в рублях или оставьте поле пустым для отображения "Цена по запросу"
                                </Form.Text>
                            </Form.Group>

                            <Form.Group className="mb-4">
                                <Form.Label>Описание</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={4}
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    placeholder="Описание товара"
                                />
                            </Form.Group>
                        </Col>

                        <Col md={4}>
                            <Card className="mb-4">
                                <Card.Header>Изображения товара</Card.Header>
                                <Card.Body>
                                    <div className="mb-3">
                                        <Form.Control
                                            type="file"
                                            accept=".jpg,.jpeg,.png"
                                            onChange={handleImageUpload}
                                            multiple
                                            disabled={formData.images.length >= 4}
                                        />
                                        <Form.Text className="text-muted">
                                            Максимум 4 изображения. Рекомендуемый размер: 800x600px.
                                        </Form.Text>
                                    </div>
                                    
                                    {formData.images.length === 0 && (
                                        <Alert variant="info">
                                            Нет загруженных изображений
                                        </Alert>
                                    )}
                                    
                                    <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
                                        <Droppable droppableId="product-images">
                                            {(provided) => (
                                                <div 
                                                    ref={provided.innerRef}
                                                    {...provided.droppableProps}
                                                    className="image-list"
                                                >
                                                    {formData.images.map((image, index) => (
                                                        <Draggable
                                                            key={image.tempId || `image-${index}`}
                                                            draggableId={image.tempId?.toString() || `image-${index}`}
                                                            index={index}
                                                        >
                                                            {(provided, snapshot) => (
                                                                <div
                                                                    ref={provided.innerRef}
                                                                    {...provided.draggableProps}
                                                                    {...provided.dragHandleProps}
                                                                    className={`product-image-item mb-2 ${snapshot.isDragging ? 'dragging' : ''}`}
                                                                >
                                                                    <div className="d-flex border rounded p-2">
                                                                        <div className="image-preview me-2">
                                                                            <img 
                                                                                src={image.imagePath || defaultProductImage} 
                                                                                alt={`Изображение ${index + 1}`}
                                                                                style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                                                                                onError={(e) => {
                                                                                    e.target.onerror = null;
                                                                                    e.target.src = defaultProductImage;
                                                                                }}
                                                                            />
                                                                        </div>
                                                                        <div className="d-flex flex-column justify-content-between flex-grow-1">
                                                                            <div className="d-flex justify-content-between">
                                                                                <div>
                                                                                    {image.isMain && (
                                                                                        <span className="badge bg-primary">Главное</span>
                                                                                    )}
                                                                                </div>
                                                                                <div>
                                                                                    <Button 
                                                                                        variant={image.isMain ? "secondary" : "outline-primary"}
                                                                                        size="sm" 
                                                                                        className="me-1"
                                                                                        onClick={() => handleSetMainImage(index)}
                                                                                        disabled={image.isMain}
                                                                                        title="Сделать главным"
                                                                                    >
                                                                                        <FaStar />
                                                                                    </Button>
                                                                                    <Button 
                                                                                        variant="outline-danger" 
                                                                                        size="sm"
                                                                                        onClick={() => handleRemoveImage(index)}
                                                                                        title="Удалить"
                                                                                    >
                                                                                        <FaTrash />
                                                                                    </Button>
                                                                                </div>
                                                                            </div>
                                                                            <div className="text-muted small mt-1">
                                                                                {index + 1} / {formData.images.length}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </Draggable>
                                                    ))}
                                                    {provided.placeholder}
                                                </div>
                                            )}
                                        </Droppable>
                                    </DragDropContext>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    <h4 className="mt-4 mb-3">Артикулы товара</h4>
                    <div className="mb-4">
                        <Table striped bordered hover responsive>
                            <thead>
                                <tr>
                                    <th style={{ width: '40%' }}>Код артикула <span className="text-danger">*</span></th>
                                    <th style={{ width: '50%' }}>Название варианта {formData.articles.length > 1 && <span className="text-danger">*</span>}</th>
                                    <th style={{ width: '10%' }}>Действия</th>
                                </tr>
                            </thead>
                            <tbody>
                                {formData.articles.map((article, index) => (
                                    <tr key={index}>
                                        <td>
                                            <Form.Control
                                                type="text"
                                                value={article.code}
                                                onChange={(e) => handleArticleChange(index, 'code', e.target.value)}
                                                isInvalid={!!validationErrors[`article_${index}_code`]}
                                                placeholder="Артикул"
                                                required
                                            />
                                        </td>
                                        <td>
                                            <Form.Control
                                                type="text"
                                                value={article.name}
                                                onChange={(e) => handleArticleChange(index, 'name', e.target.value)}
                                                placeholder={formData.articles.length === 1 ? "Необязательно" : "Название варианта"}
                                            />
                                            {formData.articles.length > 1 && (
                                                <Form.Control.Feedback type="invalid">
                                                    {validationErrors[`article_${index}_name`]}
                                                </Form.Control.Feedback>
                                            )}
                                        </td>
                                        <td className="text-center">
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={() => handleRemoveArticle(index)}
                                                title="Удалить"
                                            >
                                                <FaTrash />
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                        
                        {validationErrors.articles && (
                            <Alert variant="danger" className="mt-2 mb-3">
                                {validationErrors.articles}
                            </Alert>
                        )}
                        
                        <Button
                            variant="outline-primary"
                            onClick={handleAddArticle}
                            className="mb-3"
                        >
                            <FaPlus /> Добавить артикул
                        </Button>
                        
                        <Form.Text className="text-muted d-block">
                            Если у товара всего один артикул, название варианта можно не указывать
                        </Form.Text>
                    </div>
                    
                    {categoryCharacteristics.length > 0 && (
                        <>
                            <h4 className="mt-4 mb-3">Характеристики товара</h4>
                            <div className="mb-4">
                                <Table striped bordered hover responsive>
                                    <thead>
                                        <tr>
                                            <th style={{ width: '40%' }}>Характеристика</th>
                                            <th style={{ width: '60%' }}>Значение</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {formData.characteristics && formData.characteristics.map((char, index) => (
                                            <tr key={index}>
                                                <td>{char.name}</td>
                                                <td>
                                                    <Form.Control
                                                        type="text"
                                                        value={char.value}
                                                        onChange={(e) => handleCharacteristicChange(char.categoryCharacteristicId, e.target.value)}
                                                        placeholder="Введите значение"
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                                <Form.Text className="text-muted">
                                    Заполните только те характеристики, которые применимы к данному товару
                                </Form.Text>
                            </div>
                        </>
                    )}

                    <hr className="my-4" />

                    <div className="d-flex justify-content-center mt-4">
                        <Button
                            variant="secondary"
                            className="me-2 px-5"
                            onClick={handleCancel}
                        >
                            Отмена
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            className="px-5"
                            disabled={loading || localLoading}
                        >
                            {isNew ? 'Создать' : 'Сохранить'}
                        </Button>
                    </div> 
                </Form>
            )}
        </Container>
    );
};

export default AdminProductEdit;